// THE WORK A CONTENT WRITER SENT IN (migration 064), wherever a manager looks for it.
//
// ⚠ WHY THIS EXISTS AS ONE COMPONENT. `job_files` was added with the permissions work,
// and every screen that shows a job's or a client's files was built before it: the job
// screen, the retainer screen, the client card and the client-documents page all read the
// four OLDER file tables and knew nothing about this one. So a writer's upload was stored
// correctly — row and R2 object both — and appeared nowhere at all. Three screens needed
// the same block, so there is one block.
//
// It is DISPLAY plus two acts a manager has: download, and remove. No upload — the whole
// point of the table is that the writer puts the file there, through a mount this side of
// the app never touches.
//
// ⚠ OPENING A FILE CLEARS ITS DOORBELL. `seen_at` is what the manager's notification
// reads, so viewing or downloading marks it seen. That is the honest coupling: the bell
// asks "has anybody looked at this", and this is the looking.
import { useState } from 'react';
import he from '../i18n/he';
import { jobsApi } from '../api';
import Button from './Button';
import DateText from './DateText';
import { FileActions } from './FileList';
import { ErrorText } from './Loader';

// An unseen file is marked, and the mark is the only thing distinguishing these rows from
// any other file list. It matters because this is the one kind that arrives from OUTSIDE
// the office — nobody here filed it, so "has anyone looked" is a real question.
const UnseenDot = ({ seen }) => {
  if (seen) return null;
  return <span className="badge amber work-file-new">{he.workFiles.unseen}</span>;
};

export default function WorkFilesBlock({ files, onChange, heading, showJob = false }) {
  const [error, setError] = useState(null);
  const rows = files || [];

  // Reading a file is what "seen" means, so the two happen together. Best effort: a
  // failure to clear the doorbell must never stop the download the manager asked for.
  const markSeen = async (file) => {
    if (file.seen_at) return;
    try {
      await jobsApi.markWorkFileSeen(file.id);
      if (onChange) onChange();
    } catch {
      /* the file still opened; the bell simply stays lit */
    }
  };

  const remove = async (file) => {
    try {
      await jobsApi.removeWorkFile(file.id);
      if (onChange) onChange();
    } catch (e) {
      setError(e);
    }
  };

  return (
    <>
      {heading && <h3>{heading}</h3>}
      {rows.length === 0 ? (
        <p className="muted">{he.workFiles.none}</p>
      ) : (
        <div className="file-list">
          {rows.map((f) => (
            <div key={f.id} className="file-row">
              <UnseenDot seen={f.seen_at} />
              <span className="file-name" title={f.original_name}>
                {f.title || f.original_name}
              </span>
              <span className="muted small-note">
                {/* Who sent it — the one fact these rows carry that the office's own
                    files do not need, because for those the answer is always "us". */}
                {f.uploaded_by_user_name && `${he.workFiles.by} ${f.uploaded_by_user_name} · `}
                <DateText value={f.uploaded_at} />
                {showJob && f.job_title && ` · ${f.job_title}`}
              </span>
              {/* The same actions component every other file list uses, pointed at this
                  domain's own endpoint. No file handling is rebuilt here. */}
              <FileActions
                file={f}
                getUrl={jobsApi.workFileUrl}
                onOpened={() => markSeen(f)}
                onRemove={() => remove(f)}
              />
            </div>
          ))}
        </div>
      )}
      {error && <ErrorText error={error} />}
    </>
  );
}
